import { useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

interface ClientOption {
  id: string;
  name: string;
  code?: string | null;
}

interface CustomerClientsPickerProps {
  clients: ClientOption[];
  selectedIds: string[];
  disabled?: boolean;
  onSave: (clientIds: string[]) => void;
}

/**
 * Pick which clients (brands) a customer login can see in the portal.
 * Edits are staged while the popover is open and saved on close, so toggling
 * several brands is one save instead of one per click.
 */
export function CustomerClientsPicker({ clients, selectedIds, disabled, onSave }: CustomerClientsPickerProps) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<string[]>(selectedIds);

  useEffect(() => {
    if (!open) setDraft(selectedIds);
  }, [open, selectedIds]);

  function handleOpenChange(next: boolean) {
    if (!next) {
      const changed =
        draft.length !== selectedIds.length || draft.some((id) => !selectedIds.includes(id));
      if (changed) onSave(draft);
    }
    setOpen(next);
  }

  function toggle(id: string, checked: boolean) {
    setDraft((prev) => (checked ? [...prev, id] : prev.filter((x) => x !== id)));
  }

  const names = clients.filter((c) => selectedIds.includes(c.id)).map((c) => c.name);
  const label = names.length === 0 ? "Select clients…" : names.join(", ");

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          disabled={disabled}
          className="h-8 w-56 justify-between text-xs font-normal"
        >
          <span className={`truncate ${names.length === 0 ? "text-muted-foreground" : ""}`}>{label}</span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-2">
        <div className="max-h-72 space-y-1 overflow-y-auto">
          {clients.map((c) => {
            const id = `customer-client-${c.id}`;
            return (
              <label
                key={c.id}
                htmlFor={id}
                className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-muted"
              >
                <Checkbox
                  id={id}
                  checked={draft.includes(c.id)}
                  onCheckedChange={(v) => toggle(c.id, v === true)}
                />
                <span className="truncate">{c.name}</span>
                {c.code && <span className="ml-auto text-xs text-muted-foreground">{c.code}</span>}
              </label>
            );
          })}
        </div>
        <p className="mt-2 border-t px-2 pt-2 text-xs text-muted-foreground">
          The customer sees every selected brand in the portal.
        </p>
      </PopoverContent>
    </Popover>
  );
}
